import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import MasterEditModal from '../../components/MasterEditModal';
import { buildResultOptionMap, getResultLookupType, normalizeResultOptions } from '../../utils/resultOptions';
import { BODY_ZONE_KEY_OPTIONS, bodyZoneKeyLabel } from '../../utils/bodyZones';

export default function InspectionItems() {
  const [types, setTypes] = useState([]);
  const [inspectionTypeId, setInspectionTypeId] = useState('');
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [sortOrder, setSortOrder] = useState(1);
  const [zoneKey, setZoneKey] = useState('');
  const [supportedStyles, setSupportedStyles] = useState('sedan,suv,coupe,pickup,hatchback,van');
  const [allowedResultTypes, setAllowedResultTypes] = useState([]);
  const [lookupRows, setLookupRows] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const selectedType = types.find((t) => String(t.id) === String(inspectionTypeId));
  const selectedKey = selectedType?.key || '';
  const isBody = selectedKey === 'body';
  const resultKind = isBody ? 'body' : 'generic';
  const availableResultOptions = useMemo(() => {
    const all = normalizeResultOptions(lookupRows, resultKind);

    // ما لم يُربط بنوع الفحص لا يظهر هنا.
    // قائمة فارغة على النوع تعني «الكل متاح» (السلوك السابق).
    const linked = Array.isArray(selectedType?.allowed_result_types)
      ? selectedType.allowed_result_types.map((value) => String(value).toLowerCase())
      : [];

    if (!linked.length) return all;

    const allowed = new Set(linked);
    const filtered = all.filter((option) => allowed.has(String(option.value).toLowerCase()));
    return filtered.length ? filtered : all;
  }, [lookupRows, resultKind, selectedType?.allowed_result_types]);
  const resultOptionMap = useMemo(() => buildResultOptionMap(availableResultOptions), [availableResultOptions]);

  const loadTypes = useCallback(() => {
    api.get('/inspection-types').then((r) => {
      const supportsItems = (r.data || []).filter((t) => t.supports_items);
      setTypes(supportsItems);
      if (!inspectionTypeId && supportsItems.length > 0) {
        setInspectionTypeId(String(supportsItems[0].id));
      }
    });
  }, [inspectionTypeId]);

  const load = useCallback(() => {
    if (!selectedKey) return;
    api.get(`/inspection-items/by-section/${selectedKey}`).then((r) => setRows(r.data));
  }, [selectedKey]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedKey) return;

    api.get(`/lookups/by-type/${getResultLookupType(resultKind)}`)
      .then((r) => setLookupRows(r.data || []))
      .catch(() => setLookupRows([]));
  }, [selectedKey, resultKind]);

  useEffect(() => {
    setAllowedResultTypes(availableResultOptions.map((option) => option.value));
  }, [availableResultOptions]);

  const toggleAllowedResultType = (value) => {
    setAllowedResultTypes((prev) => {
      if (prev.includes(value)) {
        const next = prev.filter((item) => item !== value);
        return next.length ? next : [value];
      }

      return [...prev, value];
    });
  };

  const add = async () => {
    if (!name) return;

    const styles = supportedStyles
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    await api.post('/inspection-items', {
      inspection_type_id: Number(inspectionTypeId),
      section: selectedKey,
      name,
      name_en: nameEn || null,
      diagram_zone_key: isBody ? zoneKey || null : null,
      supported_body_styles: isBody ? styles : null,
      allowed_result_types: allowedResultTypes,
      sort_order: Number(sortOrder) || 0,
      is_active: true,
    });
    setName('');
    setNameEn('');
    setSortOrder(1);
    setZoneKey('');
    setAllowedResultTypes(availableResultOptions.map((option) => option.value));
    load();
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      inspection_type_id: String(row.inspection_type_id || inspectionTypeId),
      name: row.name || '',
      name_en: row.name_en || '',
      diagram_zone_key: row.diagram_zone_key || '',
      supported_body_styles: Array.isArray(row.supported_body_styles) && row.supported_body_styles.length
        ? row.supported_body_styles.join(',')
        : 'sedan,suv,coupe,pickup,hatchback,van',
      allowed_result_types: Array.isArray(row.allowed_result_types) && row.allowed_result_types.length
        ? row.allowed_result_types
        : availableResultOptions.map((option) => option.value),
      sort_order: row.sort_order ?? 0,
      is_active: Boolean(row.is_active),
    });
  };

  const update = async () => {
    if (!editForm?.inspection_type_id || !editForm?.name.trim() || !editRow) return;

    const editType = types.find((t) => String(t.id) === String(editForm.inspection_type_id));
    const editIsBody = editType?.key === 'body';
    setSaving(true);
    try {
      await api.put(`/inspection-items/${editRow.id}`, {
        inspection_type_id: Number(editForm.inspection_type_id),
        section: editType?.key || selectedKey,
        name: editForm.name,
        name_en: editForm.name_en || null,
        diagram_zone_key: editIsBody ? (editForm.diagram_zone_key || null) : null,
        supported_body_styles: editIsBody
          ? (editForm.supported_body_styles || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
        allowed_result_types: editForm.allowed_result_types || [],
        sort_order: Number(editForm.sort_order) || 0,
        is_active: editForm.is_active,
      });
      setEditRow(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('تأكيد الحذف؟')) return;
    await api.delete(`/inspection-items/${row.id}`);
    load();
  };

  return (
    <div>
      <div className="grid md:grid-cols-5 gap-2 mb-3">
        <select value={inspectionTypeId} onChange={(e) => setInspectionTypeId(e.target.value)} className="rounded-lg border p-2">
          <option value="">اختر نوع الفحص</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.icon ? `${t.icon} ` : ''}{t.name}</option>)}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg border p-2" placeholder="اسم البند" />
        <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} className="rounded-lg border p-2" placeholder="English item name" />
        <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="rounded-lg border p-2" placeholder="الترتيب" />
        <button onClick={add} disabled={!inspectionTypeId} className="rounded-lg bg-primary px-3 py-2 text-white disabled:opacity-50">إضافة</button>
      </div>

      {isBody && (
        <div className="grid md:grid-cols-2 gap-2 mb-3">
          <select
            value={zoneKey}
            onChange={(e) => setZoneKey(e.target.value)}
            className="rounded-lg border p-2"
          >
            <option value="">اختر جزء السيارة في الرسم</option>
            {BODY_ZONE_KEY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            value={supportedStyles}
            onChange={(e) => setSupportedStyles(e.target.value)}
            className="rounded-lg border p-2"
            placeholder="sedan,suv,coupe,pickup,hatchback,van"
          />
        </div>
      )}

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 text-sm font-semibold text-slate-700">النتائج الثابتة المسموح بها لهذا البند</div>
        <div className="flex flex-wrap gap-2">
          {availableResultOptions.map((option) => {
            const active = allowedResultTypes.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleAllowedResultType(option.value)}
                className={`rounded-lg border px-3 py-2 text-sm ${active ? 'border-primary bg-blue-50 text-primary' : 'border-slate-300 bg-white text-slate-600'}`}
              >
                <span className="ml-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ backgroundColor: option.bgColor }} />
                {active ? '✓ ' : ''}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <table className="w-full text-sm border rounded-lg overflow-hidden">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-2 text-right">البند</th>
            <th className="p-2 text-right">English name</th>
            <th className="p-2 text-right">النوع</th>
            <th className="p-2 text-right">الجزء في الرسم</th>
            <th className="p-2 text-right">النتائج الثابتة</th>
            <th className="p-2 text-right">الترتيب</th>
            <th className="p-2">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="p-2">{r.name}</td>
              <td className="p-2">{r.name_en || '-'}</td>
              <td className="p-2">{r.inspection_type?.name || r.section}</td>
              <td className="p-2 text-xs">
                {r.diagram_zone_key ? (
                  <>
                    <div>{bodyZoneKeyLabel(r.diagram_zone_key)}</div>
                    <div className="font-mono text-[11px] text-slate-500">{r.diagram_zone_key}</div>
                  </>
                ) : '-'}
              </td>
              <td className="p-2 text-xs">{Array.isArray(r.allowed_result_types) && r.allowed_result_types.length ? r.allowed_result_types.map((value) => resultOptionMap[value]?.label || value).join('، ') : '-'}</td>
              <td className="p-2">{r.sort_order}</td>
              <td className="p-2 flex gap-2 justify-center">
                <button onClick={() => openEdit(r)} className="px-2 py-1 border rounded">تعديل</button>
                <button onClick={() => remove(r)} className="px-2 py-1 border rounded text-red-600">حذف</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <MasterEditModal
        open={Boolean(editRow && editForm)}
        title="تعديل بند الفحص"
        subtitle="عدّل كل خانات البند ثم احفظ التغيير."
        fields={[
          {
            name: 'inspection_type_id',
            label: 'نوع الفحص',
            type: 'select',
            required: true,
            options: types.map((t) => ({ value: String(t.id), label: `${t.icon ? `${t.icon} ` : ''}${t.name}` })),
          },
          { name: 'name', label: 'اسم البند', required: true },
          { name: 'name_en', label: 'English item name' },
          { name: 'sort_order', label: 'الترتيب', type: 'number' },
          ...(types.find((t) => String(t.id) === String(editForm?.inspection_type_id))?.key === 'body'
            ? [
                {
                  name: 'diagram_zone_key',
                  label: 'الجزء في رسم السيارة',
                  type: 'select',
                  options: [
                    { value: '', label: 'بدون' },
                    ...BODY_ZONE_KEY_OPTIONS,
                  ],
                },
                {
                  name: 'supported_body_styles',
                  label: 'أشكال الهيكل المدعومة',
                  placeholder: 'sedan,suv,coupe,pickup,hatchback,van',
                  fullWidth: true,
                },
              ]
            : []),
          {
            name: 'allowed_result_types',
            label: 'النتائج الثابتة المسموح بها',
            type: 'multicheck',
            fullWidth: true,
            options: availableResultOptions.map((option) => ({
              value: option.value,
              label: option.label,
              color: option.bgColor,
            })),
          },
          { name: 'is_active', label: 'نشط', type: 'checkbox' },
        ]}
        values={editForm || {}}
        onChange={(key, value) => setEditForm((current) => ({ ...current, [key]: value }))}
        onClose={() => setEditRow(null)}
        onSubmit={update}
        saving={saving}
      />
    </div>
  );
}
